import React, { useId } from 'react';
import './SegmentedControlAtom.scss';

export interface SegmentedControlOption<T extends string = string> {
  value: T;
  label: string;
  shortLabel?: string;
  icon?: string;
  disabled?: boolean;
  /**
   * Native tooltip / accessible description surfaced on hover + via `title`. Intended for a
   * disabled option whose reason isn't visible on the surface itself (e.g. "Arriving with
   * EAINT-937"). Reflected onto the rendered `<button>` regardless of mode.
   */
  title?: string;
  /** Parity self-description (see visual-fidelity.md) — the contract datum this option renders. */
  datum?: string;
  /**
   * A count that belongs INSIDE this option, after its label — "Agent 3", "Inherited 5".
   * The strip has no badge slot, and a count rendered as a sibling of the whole control is a
   * number attached to nothing: it survives the option being hidden, and it sits in the wrong
   * container for any contract that places it within the option. Rendered only in `asTabs`
   * mode, where the option is a real tab a count can belong to. (`renderAs='button'` hardcodes
   * a radiogroup and ignores `asTabs` altogether, so it has no tab for a badge to sit in.)
   *
   * `ariaLabel` is required, not optional: without it the tab's accessible name is "Agent 3"
   * and a screen-reader user hears a bare digit counting nothing. It should name what is being
   * counted, localized and pluralized by the caller.
   */
  badge?: { label: string; ariaLabel: string; datum?: string; kind?: string; testId?: string };
  /**
   * Test/automation hook on THIS option. The root `data-testid` names the whole strip, which is
   * not enough for a caller whose assertion is that a particular option is offered or withheld
   * (the composer rail withholds kinds that are not placeable for its target). Additive: an
   * option that omits it emits no attribute, exactly as before.
   */
  testId?: string;
}

export interface SegmentedControlAtomProps<T extends string = string> {
  /** Array of options to display */
  options: SegmentedControlOption<T>[];
  /** Current selected value(s) - single value for mode="single", array for mode="multi" */
  value: T | T[];
  /** Change handler */
  onChange: (value: T | T[]) => void;
  /** Selection mode */
  mode: 'single' | 'multi';
  /** Visual variant */
  variant?: 'strip' | 'pills';
  /**
   * Selected-state treatment. `'default'` is the dark filled segment every existing caller
   * gets; `'quiet'` is a light accent tint with accent border/text (pills) or a white raised
   * segment on a grey track (strip), for controls that sit inside a card (spec rollup-combine D106).
   */
  tone?: 'default' | 'quiet';
  /** Size variant */
  size?: 'compact' | 'small' | 'normal' | 'large';
  /** Use short labels when available */
  useShortLabels?: boolean;
  /** Validation errors */
  errors?: string[];
  /** Additional CSS class */
  className?: string;
  /** Accessible label for the control group */
  ariaLabel?: string;
  /**
   * The id of a VISIBLE element that names the group, for a caller that already renders the
   * label as text beside the control (a field caption above a row of pills) and must not
   * duplicate it as an `aria-label`. Applied to the same element `ariaLabel` names.
   */
  ariaLabelledBy?: string;
  /** Test ID for testing */
  'data-testid'?: string;
  /**
   * Renders each option as a tab (`role="tab"`/`aria-selected`, the group as `role="tablist"`)
   * for a caller whose prototype source is a tab strip (e.g. the composer's side rail —
   * `type-composer.jsx:1796-1807`), rather than the plain radio-group semantics this atom
   * otherwise carries. Additive: existing radio-group callers are unaffected.
   */
  asTabs?: boolean;
  /** Contract kind stamped on every named option — see visual-fidelity.md. Defaults to `'tab'`. */
  optionKind?: string;
  /**
   * How each option is realised. `'input'` (default) wraps a native radio/checkbox in a
   * label — the historic shape, unchanged for every existing caller. `'button'` renders an
   * ARIA radiogroup of real `<button>`s with roving tabindex, for a caller whose contract
   * says the option IS a button (the sphere-type composer's `CSeg` rows). Additive: a caller
   * that does not ask for it sees no change.
   */
  renderAs?: 'input' | 'button';
  /** Parity instrumentation — `data-datum` for the root element. */
  'data-datum'?: string;
  /** Parity instrumentation — `data-kind` for the root element; defaults to `'toggle'`. */
  'data-kind'?: string;
}

/**
 * SegmentedControlAtom
 *
 * A connected strip of buttons for single or multi-select scenarios.
 * Replaces both WeekdayPickerAtom and ModeToggleAtom with a generic solution.
 *
 * @example Single select (like radio buttons)
 * <SegmentedControlAtom
 *   options={[
 *     { value: 'dayOfMonth', label: 'By date' },
 *     { value: 'weekdayOfMonth', label: 'By weekday' },
 *   ]}
 *   value={monthlyMode}
 *   onChange={setMonthlyMode}
 *   mode="single"
 * />
 *
 * @example Multi select (like checkboxes)
 * <SegmentedControlAtom
 *   options={weekdayOptions}
 *   value={selectedDays}
 *   onChange={setSelectedDays}
 *   mode="multi"
 * />
 */
export function SegmentedControlAtom<T extends string = string>({
  options,
  value,
  onChange,
  mode,
  variant = 'strip',
  tone = 'default',
  size = 'normal',
  useShortLabels = false,
  errors = [],
  className = '',
  ariaLabel,
  ariaLabelledBy,
  'data-testid': testId,
  asTabs = false,
  optionKind = 'tab',
  renderAs = 'input',
  'data-datum': datum,
  'data-kind': kind,
}: Readonly<SegmentedControlAtomProps<T>>) {
  const groupId = useId();
  const hasError = errors.length > 0;

  const isSelected = (optionValue: T): boolean => {
    if (mode === 'single') {
      return value === optionValue;
    }
    return Array.isArray(value) && value.includes(optionValue);
  };

  const handleChange = (optionValue: T) => {
    if (mode === 'single') {
      onChange(optionValue);
    } else {
      const currentValues: T[] = Array.isArray(value) ? value : [];
      if (currentValues.includes(optionValue)) {
        onChange(currentValues.filter((v) => v !== optionValue));
      } else {
        onChange([...currentValues, optionValue]);
      }
    }
  };

  const rootClasses = [
    'segmented-control',
    `segmented-control--${variant}`,
    `segmented-control--${size}`,
    tone === 'quiet' && 'segmented-control--quiet',
    hasError && 'segmented-control--error',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  const inputType = mode === 'single' ? 'radio' : 'checkbox';
  const groupName = mode === 'single' ? `segmented-control-${groupId}` : undefined;

  const enabled = options.filter((option) => !option.disabled);
  /**
   * Roving tabindex (WAI-ARIA APG radio group / tabs): the selected option is the one tab
   * stop. When NOTHING is selected — a single-select whose value matches no option, the shape
   * a date row takes while a literal date is in charge — the first enabled option becomes the
   * stop instead; otherwise every option is `tabindex=-1` and the group is unreachable by
   * keyboard.
   */
  const hasSelection = options.some((option) => !option.disabled && isSelected(option.value));
  const rovingTabIndex = (option: SegmentedControlOption<T>, selected: boolean): 0 | -1 =>
    selected || (!hasSelection && enabled[0]?.value === option.value) ? 0 : -1;
  /** Roving focus across the radiogroup — arrow keys move the selection, as ARIA requires. */
  const moveSelection = (from: T, delta: number, current: HTMLElement, focusSelector = 'button:not([disabled])') => {
    if (enabled.length === 0) return;
    const index = enabled.findIndex((option) => option.value === from);
    const nextIndex = (((index < 0 ? 0 : index) + delta) % enabled.length + enabled.length) % enabled.length;
    const next = enabled[nextIndex];
    if (!next) return;
    handleChange(next.value);
    // Roving tabindex moves with the selection, so focus has to move with it too —
    // otherwise the focused button becomes untabbable and the group loses focus.
    current.parentElement?.querySelectorAll<HTMLElement>(focusSelector)?.[nextIndex]?.focus();
  };

  if (renderAs === 'button') {
    return (
      <div className={rootClasses} data-testid={testId} data-datum={datum} data-kind={datum ? (kind ?? 'toggle') : undefined}>
        <div className="segmented-control__buttons" role="radiogroup" aria-label={ariaLabel} aria-labelledby={ariaLabelledBy}>
          {options.map((option) => {
            const selected = isSelected(option.value);
            const displayLabel = useShortLabels && option.shortLabel ? option.shortLabel : option.label;
            return (
              <button
                key={option.value}
                type="button"
                role="radio"
                aria-checked={selected}
                tabIndex={rovingTabIndex(option, selected)}
                disabled={option.disabled}
                title={option.title}
                className={`segmented-control__button ${selected ? 'segmented-control__button--selected' : ''} ${option.disabled ? 'segmented-control__button--disabled' : ''}`}
                data-datum={option.datum}
                data-container="each(options)"
                data-kind={option.datum ? optionKind : undefined}
                data-testid={option.testId}
                onClick={() => handleChange(option.value)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                    event.preventDefault();
                    moveSelection(option.value, 1, event.currentTarget);
                  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    moveSelection(option.value, -1, event.currentTarget);
                  }
                }}
              >
                {option.icon && <i className={option.icon} />}
                <span>{displayLabel}</span>
              </button>
            );
          })}
        </div>
        {hasError && (
          <ul className="segmented-control__errors">
            {errors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  if (asTabs) {
    // A tab strip, not a radio group: role="tab"/"tablist" on a <label>/<fieldset> is an
    // invalid ARIA role for those elements (axe aria-allowed-role), and a <label> wrapping a
    // native <input> is a nested interactive control once it also carries role="tab" (axe
    // nested-interactive). Real `div[role=tab]` elements with no nested input, following the
    // WAI-ARIA APG tabs pattern, avoid both — selection state lives entirely in
    // aria-selected/roving tabindex, same as the renderAs="button" radiogroup above.
    return (
      <div className={rootClasses} data-testid={testId} data-datum={datum} data-kind={datum ? (kind ?? 'toggle') : undefined}>
        <div className="segmented-control__buttons" role="tablist" aria-label={ariaLabel} aria-labelledby={ariaLabelledBy}>
          {options.map((option) => {
            const selected = isSelected(option.value);
            const displayLabel = useShortLabels && option.shortLabel ? option.shortLabel : option.label;
            return (
              <div
                key={option.value}
                role="tab"
                aria-selected={selected}
                aria-disabled={option.disabled || undefined}
                // A `role="tab"` must be focusable even when disabled (assistive tech reads
                // disabled tabs too; ARIA APG uses `tabIndex={-1}`, not omission).
                tabIndex={option.disabled ? -1 : rovingTabIndex(option, selected)}
                title={option.title}
                className={`segmented-control__button ${selected ? 'segmented-control__button--selected' : ''} ${option.disabled ? 'segmented-control__button--disabled' : ''}`}
                data-datum={option.datum}
                data-container="each(options)"
                data-kind={option.datum ? optionKind : undefined}
                data-testid={option.testId}
                onClick={() => !option.disabled && handleChange(option.value)}
                onKeyDown={(event) => {
                  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
                    event.preventDefault();
                    moveSelection(option.value, 1, event.currentTarget, '[role="tab"]:not(.segmented-control__button--disabled)');
                  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
                    event.preventDefault();
                    moveSelection(option.value, -1, event.currentTarget, '[role="tab"]:not(.segmented-control__button--disabled)');
                  } else if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    if (!option.disabled) handleChange(option.value);
                  }
                }}
              >
                {option.icon && <i className={option.icon} />}
                <span>{displayLabel}</span>
                {option.badge && (
                  <span
                    className="segmented-control__badge"
                    aria-label={option.badge.ariaLabel}
                    data-testid={option.badge.testId}
                    data-datum={option.badge.datum}
                    data-kind={option.badge.datum ? option.badge.kind ?? 'count' : undefined}
                  >
                    {option.badge.label}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {hasError && (
          <ul className="segmented-control__errors">
            {errors.map((err) => (
              <li key={err}>{err}</li>
            ))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div className={rootClasses} data-testid={testId} data-datum={datum} data-kind={datum ? (kind ?? 'toggle') : undefined}>
      <fieldset className="segmented-control__buttons" aria-label={ariaLabel} aria-labelledby={ariaLabelledBy}>
        {options.map((option) => {
          const selected = isSelected(option.value);
          const displayLabel = useShortLabels && option.shortLabel ? option.shortLabel : option.label;
          const inputId = `${groupId}-${option.value}`;

          return (
            <label
              key={option.value}
              htmlFor={inputId}
              title={option.title}
              className={`segmented-control__button ${selected ? 'segmented-control__button--selected' : ''} ${option.disabled ? 'segmented-control__button--disabled' : ''}`}
              data-container="each(options)"
              data-testid={option.testId}
            >
              <input
                type={inputType}
                id={inputId}
                name={groupName}
                value={option.value}
                checked={selected}
                disabled={option.disabled}
                title={option.title}
                onChange={() => handleChange(option.value)}
                className="segmented-control__input"
              />
              {option.icon && <i className={option.icon} />}
              <span>{displayLabel}</span>
            </label>
          );
        })}
      </fieldset>

      {hasError && (
        <ul className="segmented-control__errors">
          {errors.map((err) => (
            <li key={err}>{err}</li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SegmentedControlAtom;
